import usePeriodLevels from "backend/hooks/model/usePeriodLevels";
import { IPeriodLevel } from "backend/model/model";
import { t } from "i18next";
import * as conf from "qpr-base/configuration";
import { TValueTableSeries } from "qpr-base/configuration/types";
import React from "react";
import { Value } from "./KPIsForm/index.dto";
import useFormStyles from "./KPIsForm/themes";
import SimpleFormTable, { SimpleCell } from "./SubItems/formTable";

export const getProjectPeriods = (
  periodLevelId: string,
  periodLevels: IPeriodLevel[],
  startDate?: string, //assume yyyy-mm-dd
  endDate?: string
): string[] => {
  const level = periodLevels?.find((level) => level.id === periodLevelId);
  if (!level) return [];
  let periods = level.periods;
  if (startDate && endDate) {
    // filter with given date interval
    periods = level.periods.filter((period) => {
      return (
        new Date(period.startdate.substring(0, 10)) >= new Date(startDate) &&
        new Date(period.enddate.substring(0, 10)) <= new Date(endDate)
      );
    });
  }
  return periods.map((p) => p.name);
};

type Props = {
  isProject?: boolean;
  disabled?: boolean;
  projectDates: [string, string];
  valuesettings: { id: string; name: string; typename: string }[];
  valueSettingId: string;
  periodlevelId: string;
  projectValues?: any; // Projects[];
  kpiValues?: Value[];
  unitId: string;
  units: { id: string; name: string }[];
  visiblePeriods: string[];
};

const ValuesForm = (props: Props) => {
  const classes = useFormStyles();
  const { data: periodLevels, isLoading: isLoadingPeriodLevels } =
    usePeriodLevels();

  if (isLoadingPeriodLevels) return null;

  const getFieldType = (
    seriesSettings: TValueTableSeries
  ): "numberField" | "stringField" | "dateField" => {
    if (seriesSettings.type === "number") return "numberField";
    if (seriesSettings.type === "text") return "stringField";
    if (seriesSettings.type === "date") return "dateField";
    return "numberField";
  };

  const valSetName =
    props.valuesettings.find((vs) => vs.id === props.valueSettingId)?.name ||
    "";
  const series =
    conf.getKpiEditingViewValueSettings().find((vs) => vs.name === valSetName)
      ?.series || [];

  // KPI Values
  const kpiRows: any[] = [];
  for (let i = 0; i < series.length; i += 1) {
    const seriesSymbol = series[i].symbol;
    const row: any[] = [];
    const fieldType = getFieldType(series[i]);
    row.push(t(`configtypes.${seriesSymbol}`));

    for (let col = 0; col < props.visiblePeriods.length; col += 1) {
      const cell: SimpleCell = {
        type: fieldType,
        name: `values[${col}].${seriesSymbol}`,
        suffix: "",
        header: props.visiblePeriods[col] || "",
        disabled: series[i].readonly,
        ...{
          ...(props.kpiValues
            ? {
                value: props.kpiValues[col]?.value || "",
              }
            : {}),
        },
      };
      row.push(cell);
    }
    kpiRows.push(row);
  }

  const currentUnit = props.units.find((u: any) => u.id === props.unitId);

  // Project Values
  return (
    <div style={{ padding: "20px 0 0 0" }}>
      <h1 className={classes.formTitle}>{t("kpis.Values")}</h1>
      <SimpleFormTable
        headers={[t("configtypes.Series"), ...props.visiblePeriods]}
        rows={
          props.isProject
            ? [
                [
                  `${t("initiatives.Planned Progress")} %`,
                  ...props.visiblePeriods.map(
                    (period, ind) =>
                      ({
                        type: "numberField",
                        name: `values[PLANNED_COMPLETION###${period}]`,
                        disabled: false,
                        suffix: "%",
                      } as SimpleCell)
                  ),
                ],
                [
                  `${t("initiatives.Actual Progress")} %`,
                  ...props.visiblePeriods.map(
                    (period, ind) =>
                      ({
                        type: "numberField",
                        name: `values[COMPLETION_PERCENTAGE###${period}]`,
                        suffix: "%",
                        disabled: false,
                      } as SimpleCell)
                  ),
                ],
                [
                  `${t("initiatives.Project Start Date")}`,
                  ...props.visiblePeriods.map(
                    (period, ind) =>
                      ({
                        type: "dateField",
                        name: `values[ACTUAL_START_DATE###${period}]`,
                        suffix: "%",
                        disabled: false,
                        maxDate:
                          props.projectValues[`ACTUAL_END_DATE###${period}`],
                      } as SimpleCell)
                  ),
                ],
                [
                  `${t("initiatives.Project End Date")}`,
                  ...props.visiblePeriods.map(
                    (period, ind) =>
                      ({
                        type: "dateField",
                        name: `values[ACTUAL_END_DATE###${period}]`,
                        suffix: "%",
                        disabled: false,
                        minDate:
                          props.projectValues[`ACTUAL_START_DATE###${period}`],
                      } as SimpleCell)
                  ),
                ],
                /* Not clear if Budget is needed in the table
                [
                  `${t("initiatives.Project Budget")}`,
                  ...props.visiblePeriods.map(
                    (period, ind) =>
                      ({
                        type: "numberField",
                        name: `values[BUDGET###${period}]`,
                        disabled: false,
                        suffix: currentUnit
                          ? t(`units.${currentUnit.name}`, currentUnit.name)
                          : "",
                      } as SimpleCell)
                  ),
                ],
                */
                [
                  `${t("initiatives.Project Expenditure")}`,
                  ...props.visiblePeriods.map(
                    (period, ind) =>
                      ({
                        type: "numberField",
                        name: `values[EXPENDITURE###${period}]`,
                        suffix: currentUnit
                          ? t(`units.${currentUnit.name}`, currentUnit.name)
                          : "",
                        disabled: false,
                      } as SimpleCell)
                  ),
                ],
              ]
            : [...kpiRows]
        }
        {...props}
      />
    </div>
  );
};
export default ValuesForm;
